import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, TrendingUp, Heart, Zap, Gift, Star, ChevronRight, ShoppingCart, Send, MessageCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Function to parse basic markdown (bold, italic) to HTML
const parseMarkdown = (text) => {
  if (!text) return '';
  return text
    // Bold: **text** or __text__
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic: *text* or _text_
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Line breaks
    .replace(/\n/g, '<br/>');
};

// Sound effects for chat
const playSound = (type) => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContext();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    if (type === 'send') {
      // Send message sound - quick ascending tone
      oscillator.frequency.setValueAtTime(400, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(600, audioCtx.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.15);
    } else if (type === 'receive') {
      // Receive message sound - friendly double pop
      oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
      oscillator.frequency.setValueAtTime(1000, audioCtx.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.2);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.2);
    } else if (type === 'open') {
      // Open chat sound - welcoming tone
      oscillator.frequency.setValueAtTime(523, audioCtx.currentTime); // C5
      oscillator.frequency.setValueAtTime(659, audioCtx.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(784, audioCtx.currentTime + 0.2); // G5
      gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.3);
    }
  } catch (e) {
    // Audio not supported or blocked
    console.log('Audio not available');
  }
};

// Froggy Assistant Component - AI-powered sales assistant with Chat
export const FroggyAssistant = ({ 
  customer, 
  products, 
  cart, 
  onAddToCart, 
  onClose,
  purchaseHistory = []
}) => {
  const [currentTip, setCurrentTip] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  
  // Chat state
  const [isChatMode, setIsChatMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Send message to Froggy
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    const userMsg = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsLoading(true);
    playSound('send'); // Sound when sending
    
    try {
      const response = await axios.post(`${API_URL}/api/froggy/chat`, {
        message: userMsg,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      playSound('receive'); // Sound when receiving
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '¡Ups! 🐸 Tuve un problemita. ¿Podrías intentar de nuevo?' 
      }]);
      playSound('receive');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Initialize chat with greeting
  const startChat = () => {
    setIsChatMode(true);
    playSound('open'); // Sound when opening chat
    if (messages.length === 0) {
      const greeting = customer 
        ? `¡Hola ${customer.name?.split(' ')[0]}! 🐸 Soy Froggy, tu asistente de Mr. Frogs. ¿En qué te puedo ayudar hoy? Puedo recomendarte platillos, explicarte nuestro menú o contarte sobre nuestro programa VIP.`
        : `¡Hola! 🐸 Soy Froggy, tu asistente de Mr. Frogs Mariscos. ¿Qué se te antoja hoy? Puedo ayudarte a elegir los mejores platillos o contarte sobre nuestro programa de puntos.`;
      setMessages([{ role: 'assistant', content: greeting }]);
    }
  };

  // Generate smart recommendations based on customer history
  useEffect(() => {
    if (customer && purchaseHistory.length > 0) {
      generateSmartRecommendation();
    } else {
      generatePopularRecommendation();
    }
  }, [customer, purchaseHistory, products]);

  const generateSmartRecommendation = () => {
    // Find most purchased category
    const categoryCount = {};
    purchaseHistory.forEach(item => {
      const cat = item.category || 'general';
      categoryCount[cat] = (categoryCount[cat] || 0) + 1;
    });
    
    const favoriteCategory = Object.keys(categoryCount).sort((a, b) => categoryCount[b] - categoryCount[a])[0];
    
    // Find a product from favorite category not in cart
    const cartIds = cart.map(c => c.product_id);
    const suggestion = products.find(p => 
      p.category_name?.toLowerCase().includes(favoriteCategory?.toLowerCase()) && 
      !cartIds.includes(p.product_id)
    );
    
    if (suggestion) {
      setRecommendation({
        type: 'personalized',
        product: suggestion,
        message: `¡Te encantará! Basado en tus gustos`
      });
    }
  };

  const generatePopularRecommendation = () => {
    const cartIds = cart.map(c => c.product_id);
    const popular = products.find(p => p.is_popular && !cartIds.includes(p.product_id));
    if (popular) {
      setRecommendation({
        type: 'popular',
        product: popular,
        message: `🔥 Nuestro más vendido`
      });
    }
  };

  const tips = customer ? [
    `¡Hola ${customer.name?.split(' ')[0]}! 🎉 Tienes ${customer.points || 0} puntos`,
    customer.points >= 100 ? `¡Wow! Puedes canjear un regalo ahora mismo 🎁` : `Te faltan ${100 - (customer.points || 0)} puntos para tu primer regalo`,
    `Los clientes como tú aman nuestros mariscos frescos 🦐`
  ] : [
    `¡Bienvenido a Mr. Frogs! 🐸`,
    `Prueba nuestros platillos más populares 🔥`,
    `¡Registra tu número y acumula puntos! ⭐`
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % tips.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [tips.length]);

  return (
    <div className={`fixed bottom-24 left-4 z-40 transition-all duration-500 ${isExpanded ? 'w-72' : 'w-auto'}`}>
      {/* Froggy Avatar */}
      <div 
        className="relative cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Speech Bubble */}
        <div className={`absolute bottom-full left-12 mb-2 transition-all duration-300 ${isExpanded ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
          <div className="bg-white rounded-2xl rounded-bl-none px-4 py-2 shadow-lg max-w-[200px] animate-pulse">
            <p className="text-sm text-gray-800 font-medium">{tips[currentTip]}</p>
          </div>
          <div className="absolute -bottom-1 left-0 w-3 h-3 bg-white transform rotate-45"></div>
        </div>

        {/* Froggy Character */}
        <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform border-4 border-white">
          <span className="text-3xl">🐸</span>
        </div>
        
        {/* Notification Badge */}
        {recommendation && !isExpanded && (
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-ping">
            <span className="text-white text-xs font-bold">!</span>
          </div>
        )}
      </div>

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="absolute bottom-20 left-0 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🐸</span>
                </div>
                <div>
                  <h3 className="font-bold">Froggy</h3>
                  <p className="text-xs text-white/80">
                    {isChatMode ? 'Chatea conmigo' : 'Tu asistente de pedidos'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isChatMode && (
                  <button 
                    onClick={() => setIsChatMode(false)} 
                    className="text-white/80 hover:text-white text-xs bg-white/20 px-2 py-1 rounded"
                  >
                    Tips
                  </button>
                )}
                <button onClick={() => setIsExpanded(false)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Chat Mode */}
          {isChatMode ? (
            <div className="flex flex-col h-80">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
                {messages.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                      msg.role === 'user' 
                        ? 'bg-green-500 text-white rounded-br-sm' 
                        : 'bg-white text-gray-800 shadow-sm rounded-bl-sm'
                    }`}>
                      <p 
                        className="text-sm" 
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }}
                      />
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white text-gray-800 shadow-sm rounded-2xl rounded-bl-sm px-4 py-2">
                      <Loader2 className="w-5 h-5 animate-spin text-green-500" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Input */}
              <div className="p-3 border-t bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Escríbeme algo..."
                    className="flex-1 px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={isLoading}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={isLoading || !inputMessage.trim()}
                    className="bg-green-500 text-white p-2 rounded-full hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Customer Status */}
              {customer && (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-3 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">¡Hola {customer.name?.split(' ')[0]}!</p>
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                        {customer.points || 0} puntos acumulados
                      </p>
                    </div>
                    {customer.points >= 50 && (
                      <div className="bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                        🎁 Canjea
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Smart Recommendation */}
              {recommendation && (
                <div className="p-3">
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-amber-500" />
                    {recommendation.message}
                  </p>
                  <div 
                    className="flex items-center gap-3 p-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl cursor-pointer hover:shadow-md transition-all"
                    onClick={() => {
                      onAddToCart(recommendation.product);
                      setRecommendation(null);
                      generatePopularRecommendation();
                    }}
                  >
                    {recommendation.product.image_url ? (
                      <img 
                        src={recommendation.product.image_url} 
                        alt="" 
                        className="w-14 h-14 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-orange-200 flex items-center justify-center">
                        <span className="text-2xl">🍤</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-800">{recommendation.product.name}</p>
                      <p className="text-orange-600 font-bold">${recommendation.product.price}</p>
                    </div>
                    <button className="bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition-colors">
                      <ShoppingCart className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Tips */}
              <div className="p-3 bg-gray-50 border-t">
                <p className="text-xs text-gray-500 mb-2">💡 Sabías que...</p>
                <p className="text-sm text-gray-700">{tips[currentTip]}</p>
              </div>

              {/* Chat Button */}
              <div className="p-3 border-t">
                <button
                  onClick={startChat}
                  className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-2 rounded-full hover:bg-green-600 transition-colors text-sm font-medium"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chatea conmigo
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Smart Recommendations Section
export const SmartRecommendations = ({ 
  products, 
  cart, 
  customer,
  purchaseHistory = [],
  onAddToCart 
}) => {
  const [activeTab, setActiveTab] = useState('popular');
  
  const cartIds = cart.map(c => c.product_id);
  
  // Calculate recommendations
  const getPopular = () => products.filter(p => p.is_popular && !cartIds.includes(p.product_id)).slice(0, 4);
  
  const getFavorites = () => {
    if (!purchaseHistory.length) return [];
    const productCounts = {};
    purchaseHistory.forEach(item => {
      productCounts[item.product_id] = (productCounts[item.product_id] || 0) + item.quantity;
    });
    const sortedIds = Object.keys(productCounts).sort((a, b) => productCounts[b] - productCounts[a]);
    return sortedIds
      .map(id => products.find(p => p.product_id === id))
      .filter(p => p && !cartIds.includes(p.product_id))
      .slice(0, 4);
  };
  
  const getComplementary = () => {
    // If cart has main dishes, suggest drinks. If drinks, suggest food.
    const hasMainDish = cart.some(item => {
      const product = products.find(p => p.product_id === item.product_id);
      return product?.kitchen_station === 'caliente' || product?.kitchen_station === 'fria';
    });
    
    if (hasMainDish) {
      return products.filter(p => 
        (p.category_name?.toLowerCase().includes('bebida') || p.category_name?.toLowerCase().includes('drink')) &&
        !cartIds.includes(p.product_id)
      ).slice(0, 4);
    }
    
    return products.filter(p => 
      p.is_popular && 
      !cartIds.includes(p.product_id)
    ).slice(0, 4);
  };

  const tabs = [
    { id: 'popular', label: '🔥 Más Pedidos', icon: TrendingUp },
    { id: 'favorites', label: '❤️ Tus Favoritos', icon: Heart },
    { id: 'complete', label: '✨ Complementa', icon: Zap }
  ];

  const getProducts = () => {
    switch (activeTab) {
      case 'popular': return getPopular();
      case 'favorites': return getFavorites();
      case 'complete': return getComplementary();
      default: return getPopular();
    }
  };

  const displayProducts = getProducts();

  if (displayProducts.length === 0 && activeTab === 'favorites') {
    return null; // Don't show if no favorites
  }

  return (
    <div className="bg-gradient-to-r from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-white/10">
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-amber-400" />
        <h3 className="text-lg font-bold text-white">Recomendado para ti</h3>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Products Grid */}
      {displayProducts.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {displayProducts.map(product => (
            <div 
              key={product.product_id}
              className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden hover:bg-white/20 transition-all cursor-pointer group"
              onClick={() => onAddToCart(product)}
            >
              <div className="relative h-24">
                {product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                    <span className="text-3xl">🍽️</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="bg-white rounded-full p-2">
                    <ShoppingCart className="w-5 h-5 text-orange-500" />
                  </div>
                </div>
              </div>
              <div className="p-2">
                <p className="text-white text-sm font-medium truncate">{product.name}</p>
                <p className="text-amber-400 font-bold">${product.price}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-white/60">
          <p>Explora nuestro menú para descubrir favoritos</p>
        </div>
      )}
    </div>
  );
};

// Upselling Modal at Checkout
export const UpsellModal = ({ 
  isOpen, 
  cart, 
  products, 
  onAddItem, 
  onSkip, 
  cartTotal 
}) => {
  if (!isOpen) return null;

  const cartIds = cart.map(c => c.product_id);
  
  // Smart upsell suggestions
  const suggestions = [];
  
  // 1. Suggest drinks if no drinks in cart
  const hasDrink = cart.some(item => {
    const product = products.find(p => p.product_id === item.product_id);
    return product?.category_name?.toLowerCase().includes('bebida');
  });
  
  if (!hasDrink) {
    const drink = products.find(p => 
      p.category_name?.toLowerCase().includes('bebida') && 
      !cartIds.includes(p.product_id) &&
      p.price <= 60
    );
    if (drink) {
      suggestions.push({
        product: drink,
        reason: '¿Algo para tomar?',
        discount: 10,
        icon: '🍹'
      });
    }
  }

  // 2. Suggest popular item if cart is small
  if (cartTotal < 200) {
    const popular = products.find(p => 
      p.is_popular && 
      !cartIds.includes(p.product_id) &&
      p.price <= 150
    );
    if (popular) {
      suggestions.push({
        product: popular,
        reason: 'Nuestro favorito',
        discount: 15,
        icon: '🔥'
      });
    }
  }

  // 3. Suggest dessert or extra
  const dessert = products.find(p => 
    (p.category_name?.toLowerCase().includes('postre') || p.name?.toLowerCase().includes('postre')) &&
    !cartIds.includes(p.product_id)
  );
  if (dessert) {
    suggestions.push({
      product: dessert,
      reason: 'El toque final perfecto',
      discount: 20,
      icon: '🍮'
    });
  }

  // If no suggestions, skip directly to checkout
  if (suggestions.length === 0) {
    // Call onSkip in next tick to avoid state update during render
    setTimeout(() => onSkip?.(), 0);
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Gift className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold">¡Espera!</h2>
          <p className="text-white/90">Ofertas especiales solo para ti</p>
        </div>

        {/* Suggestions */}
        <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
          {suggestions.map((item, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-4 p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border-2 border-orange-100 hover:border-orange-300 transition-colors cursor-pointer"
              onClick={() => onAddItem(item.product)}
            >
              <div className="text-4xl">{item.icon}</div>
              <div className="flex-1">
                <p className="text-xs text-orange-600 font-medium">{item.reason}</p>
                <p className="font-bold text-gray-800">{item.product.name}</p>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-green-600">
                    ${(item.product.price * (1 - item.discount/100)).toFixed(0)}
                  </span>
                  <span className="text-sm text-gray-400 line-through">
                    ${item.product.price}
                  </span>
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    -{item.discount}%
                  </span>
                </div>
              </div>
              <button className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full font-medium transition-colors">
                Agregar
              </button>
            </div>
          ))}
        </div>

        {/* Skip Button */}
        <div className="p-4 border-t">
          <button 
            onClick={onSkip}
            className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            No gracias, continuar al pago →
          </button>
        </div>
      </div>
    </div>
  );
};

// Gamification Progress Bar
export const LoyaltyProgress = ({ customer, nextReward = 100 }) => {
  if (!customer) return null;
  
  const points = customer.points || 0;
  const progress = Math.min((points / nextReward) * 100, 100);
  const pointsNeeded = Math.max(0, nextReward - points);

  return (
    <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-sm rounded-2xl p-4 border border-amber-500/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
            <Star className="w-4 h-4 text-white fill-white" />
          </div>
          <div>
            <p className="text-white font-bold">{points} puntos</p>
            <p className="text-white/60 text-xs">Cliente VIP</p>
          </div>
        </div>
        {points >= nextReward ? (
          <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
            🎁 ¡Canjea ahora!
          </div>
        ) : (
          <div className="text-right">
            <p className="text-amber-400 font-bold text-sm">{pointsNeeded} pts más</p>
            <p className="text-white/60 text-xs">para tu regalo</p>
          </div>
        )}
      </div>
      
      {/* Progress Bar */}
      <div className="relative h-3 bg-white/20 rounded-full overflow-hidden">
        <div 
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-1000"
          style={{ width: `${progress}%` }}
        />
        <div 
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full animate-pulse opacity-50"
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Milestones */}
      <div className="flex justify-between mt-2 text-xs text-white/60">
        <span>0</span>
        <span className="flex items-center gap-1">
          <Gift className="w-3 h-3" />
          {nextReward}
        </span>
      </div>
    </div>
  );
};

// Achievement Badge Component
export const AchievementBadge = ({ type, label, unlocked = false }) => {
  const badges = {
    'first-order': { icon: '🎉', color: 'from-pink-500 to-rose-500', name: 'Primera Orden' },
    'seafood-lover': { icon: '🦐', color: 'from-cyan-500 to-blue-500', name: 'Amante del Mar' },
    'vip': { icon: '👑', color: 'from-amber-500 to-yellow-500', name: 'Cliente VIP' },
    'frequent': { icon: '🔥', color: 'from-orange-500 to-red-500', name: 'Cliente Frecuente' },
    'big-spender': { icon: '💎', color: 'from-purple-500 to-indigo-500', name: 'Gran Gastador' }
  };

  const badge = badges[type] || badges['first-order'];

  return (
    <div className={`relative ${!unlocked ? 'opacity-40 grayscale' : ''}`}>
      <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${badge.color} flex items-center justify-center shadow-lg ${unlocked ? 'animate-pulse' : ''}`}>
        <span className="text-2xl">{badge.icon}</span>
      </div>
      {unlocked && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
          <span className="text-white text-xs">✓</span>
        </div>
      )}
      <p className="text-center text-xs text-white/80 mt-1">{badge.name}</p>
    </div>
  );
};

export default {
  FroggyAssistant,
  SmartRecommendations,
  UpsellModal,
  LoyaltyProgress,
  AchievementBadge
};
